import React, { useEffect, useRef, useState } from 'react';
import {
  Badge, Box, Button, Center, HStack, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiPaperclip, FiTrash2, FiDownload, FiExternalLink,
} from 'react-icons/fi';
import {
  StoredFileScope, StoredFileInfo, useStoredFiles, useStoredFileActions,
  downloadStoredFile, viewStoredFile, getStoredFileObjectUrl,
} from '../hooks/useStoredFiles';
import { pdfObjectUrl, previewPdf } from '../utils/openPdf';
import looksLikeBasixDocument from '../utils/clientPoFile'; // item 1363/1581
import { useConfirm } from './ConfirmProvider';

// item 807 (Chad) — inline embed of a GENERATED document PDF (e.g. our outbound PO the
// manufacturer will receive). Authed blob → object URL → iframe; revoked on unmount/path change.
const PdfPathPreview: React.FC<{ path: string }> = ({ path }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    setUrl(null); setFailed(false);
    pdfObjectUrl(path).then((u) => {
      if (live) { made = u; setUrl(u); } else { URL.revokeObjectURL(u); }
    }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [path]);
  if (failed) {
    return (
      <Button variant="ghost" size="sm" leftIcon={<FiExternalLink />} onClick={() => previewPdf(path)}>
        Open PDF in a new tab
      </Button>
    );
  }
  if (!url) return <Center h="360px"><Spinner /></Center>;
  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" overflow="hidden">
      <Box as="iframe" title="PO preview" src={url} w="100%" h="440px" border="none" />
    </Box>
  );
};

// item 805 (Chad) — inline preview of the uploaded PO so you can eyeball the right document
// before moving on. Images render as <img>; PDFs embed in an <iframe>; anything else offers a
// tab open. Auth'd blob fetched to an object URL, revoked on unmount / file change.

const FilePreview: React.FC<{ file: StoredFileInfo }> = ({ file }) => {
  const [url, setUrl] = useState<string | null>(null);
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    getStoredFileObjectUrl(file.id).then((u) => {
      if (live) { made = u; setUrl(u); } else { URL.revokeObjectURL(u); }
    }).catch(() => { /* preview is best-effort */ });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [file.id]);
  const ct = (file.contentType || '').toLowerCase();
  const isImg = ct.startsWith('image/');
  const isPdf = ct.includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
  if (!url) return <Center h="120px"><Spinner /></Center>;
  return (
    <Box mt={3} borderWidth="1px" borderColor={border} borderRadius="md" overflow="hidden">
      {isImg && <Image src={url} maxH="360px" w="100%" objectFit="contain" bg="blackAlpha.300" />}
      {!isImg && isPdf && <Box as="iframe" title={file.name} src={url} w="100%" h="360px" border="none" />}
      {!isImg && !isPdf && (
        <Button variant="ghost" size="sm" leftIcon={<FiExternalLink />} m={3} onClick={() => viewStoredFile(file.id)}>
          {`Open ${file.name}`}
        </Button>
      )}
    </Box>
  );
};

// item 797 (Chad) — the "book the client's PO" prompt. A focused modal that uploads the
// client's original purchase order and attaches it (scope=ReceivedPurchaseOrder, RefId =
// the doc it's captured on: the PROPOSAL at approval, or the PURCHASE ORDER on save). The
// chain resolver carries it downstream (proposal→WO→invoice; PO→SO/WO→invoice), so it only
// needs to be uploaded ONCE, here.
const ClientPoUploadModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  docId: string;
  title?: string;
  intro?: string;
  // item 799 (Chad) — when provided, the footer shows a "Next" button (the guided PO flow:
  // upload → send PO to manufacturer → send client confirmation). onClose is still "close".
  onNext?: () => void;
  // item 807 (Chad) — the GENERATED outbound PO PDF path (e.g. `PurchaseOrder/{id}/pdf`).
  // Step 2 previews THIS (what the manufacturer receives), not the client's uploaded file.
  previewPdfPath?: string;
}> = ({
  isOpen, onClose, docId, title, intro, onNext, previewPdfPath,
}) => {
  const { data: files } = useStoredFiles(StoredFileScope.ReceivedPurchaseOrder, docId);
  const confirm = useConfirm(); // item 1581 — the Basix-document guard asks, never hard-blocks
  const { upload, remove, isUploading } = useStoredFileActions(StoredFileScope.ReceivedPurchaseOrder, docId);
  const fileRef = useRef<HTMLInputElement>(null);
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const has = (files?.length ?? 0) > 0;

  // item 806 (Chad) — in the guided PO flow (onNext present) this modal is an explicit
  // two-step: Step 1 Upload → Step 2 Preview → hand off to the manufacturer send. Every open
  // resets to step 1. The approval-wizard usage (no onNext) stays a single upload view.
  const [step, setStep] = useState<1 | 2>(1);
  useEffect(() => { if (isOpen) setStep(1); }, [isOpen]);
  const stepped = !!onNext;

  const stepLabel = (n: 1 | 2, label: string) => {
    const active = step === n;
    return (
      <HStack spacing={2} opacity={active ? 1 : 0.5}>
        <Center
          w="20px"
          h="20px"
          borderRadius="full"
          fontSize="xs"
          fontWeight="bold"
          bg={active ? 'brandGreen.500' : 'transparent'}
          borderWidth={active ? 0 : '1px'}
          borderColor={border}
          color={active ? 'white' : sub}
        >
          {n}
        </Center>
        <Text fontSize="sm" fontWeight={active ? 'semibold' : 'normal'}>{label}</Text>
      </HStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title ?? "Book the client's PO"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {stepped && (
            <HStack spacing={4} mb={4}>
              {stepLabel(1, 'Upload')}
              <Box flex="1" h="1px" bg={border} />
              {stepLabel(2, 'Preview our PO')}
            </HStack>
          )}

          {/* STEP 1 — UPLOAD (or the whole single view when not stepped) */}
          {(!stepped || step === 1) && (
            <>
              <Text fontSize="sm" color={sub} mb={3}>
                {intro ?? 'Upload the purchase order the CLIENT sent you — we keep it as the '
                  + 'reference for this job (it carries to the work order + invoice, and is NEVER '
                  + 'sent to the manufacturer). Next you\'ll preview OUR outbound PO before sending.'}
              </Text>
              <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3}>
                <HStack spacing={2} wrap="wrap">
                  {(files ?? []).map((f) => (
                    <Badge key={f.id} variant="subtle" colorScheme="green" cursor="pointer" onClick={() => downloadStoredFile(f.id, f.name)}>
                      <HStack spacing={1}>
                        <FiDownload size={11} />
                        <Text fontSize="xs">{f.name}</Text>
                        <Box as="span" aria-label="Remove" onClick={(e) => { e.stopPropagation(); remove(f.id); }}>
                          <FiTrash2 size={11} />
                        </Box>
                      </HStack>
                    </Badge>
                  ))}
                  {!has && <Text fontSize="xs" color={sub}>Nothing uploaded yet.</Text>}
                </HStack>
                <Button
                  mt={3}
                  size="sm"
                  leftIcon={<FiPaperclip />}
                  isLoading={isUploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {has ? 'Replace / add another' : 'Upload PO'}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    // item 1363 (Chad: "sends the client only one PDF") — this slot is for the CLIENT'S
                    // purchase order; one of OUR generated documents (INV-/WO-/PROP-…) got uploaded here
                    // and was mailed back to the customer beside the real invoice.
                    // item 1581 — only OUR number shape trips it now, and it ASKS instead of refusing
                    // (a customer's "PO-261020-004.pdf" was hard-blocked as "a Basix document").
                    if (looksLikeBasixDocument(f.name)) {
                      const ok = await confirm(
                        `"${f.name}" has one of our document numbers in its name. This slot is for the purchase order the CLIENT sent you. Upload it anyway?`,
                        { confirmLabel: 'Upload anyway' },
                      );
                      if (!ok) return;
                    }
                    upload(f);
                  }}
                />
                {/* item 805 — inline preview here too when NOT the stepped guided flow */}
                {!stepped && has && files && <FilePreview file={files[0]} />}
              </Box>
            </>
          )}

          {/* STEP 2 — PREVIEW OUR OUTBOUND PO (what the manufacturer receives; the client's
              uploaded PO is NEVER sent to the vendor — it's just our reference). */}
          {stepped && step === 2 && (
            <>
              <Text fontSize="sm" color={sub} mb={3}>
                This is the purchase order we&apos;ll email the manufacturer. Verify it, then send.
              </Text>
              {previewPdfPath
                ? <PdfPathPreview path={previewPdfPath} />
                : <Text fontSize="sm" color={sub}>Preview unavailable.</Text>}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {stepped ? (
            <HStack spacing={3}>
              {step === 1 ? (
                <>
                  <Button variant="ghost" onClick={onClose}>Close</Button>
                  <Button colorScheme="green" onClick={() => setStep(2)}>Next — preview our PO</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
                  <Button colorScheme="green" onClick={onNext}>Next — send PO to manufacturer</Button>
                </>
              )}
            </HStack>
          ) : (
            <Button variant="ghost" onClick={onClose}>{has ? 'Done' : 'Skip for now'}</Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

ClientPoUploadModal.defaultProps = {
  title: undefined, intro: undefined, onNext: undefined, previewPdfPath: undefined,
};

export default ClientPoUploadModal;
